import { ChangeVehicleInterstate } from '@makemydeal/dr-activities-change-vehicle';
import type { IDxgVehicle, IDxgVehicleChromeStyleId, IDxgVehicleCodes } from '@makemydeal/dr-shared-types';

import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import { OpenDealCardHeader } from '../openDealCardHeader/OpenDealCardHeader';
import { SelectedVehicleInfo } from './SelectedVehicleInfo';
import { ChangeVehicleTitle, StyledVehicleSelect } from './VehicleSelect.style';
import { OpenDealCardInfo } from '../Activity.style';

export type VehicleSelectProps = {
    selectedVehicle: IDxgVehicle | undefined;
    editMode: boolean;
    onEditModeChange: () => void;
    hideAction?: boolean;
};

const isChromeStyleIdCode = (code: IDxgVehicleCodes): code is IDxgVehicleChromeStyleId => {
    return code.codeType === 'ChromeStyleId';
};

const isVehicleValid = (vehicle?: IDxgVehicle): vehicle is IDxgVehicle => {
    const vin = vehicle?.vin;
    const chromeStyleId = vehicle?.vehicleCodes?.find(isChromeStyleIdCode)?.chromeStyleId;

    return Boolean(vin || chromeStyleId);
};

export const VehicleSelect = ({ selectedVehicle, editMode, onEditModeChange, hideAction }: VehicleSelectProps) => {
    const theme = useInterstateTheme();
    const hasVehicleSelected = isVehicleValid(selectedVehicle);
    const actionText = hasVehicleSelected ? 'Change Vehicle' : 'Add Vehicle';

    return (
        <StyledVehicleSelect
            sx={{ backgroundColor: editMode || !hasVehicleSelected ? theme.tokens.BaseColorGray50 : theme.tokens.BaseColorGreen50 }}
            data-testid="vehicle-select"
            header={
                <OpenDealCardHeader
                    title={hasVehicleSelected ? 'Vehicle' : 'Vehicle (required)'}
                    showIcon
                    isComplete={hasVehicleSelected}
                    action={hideAction ? undefined : editMode ? 'Close' : hasVehicleSelected ? 'Edit' : 'Start'}
                    onActionClick={onEditModeChange}
                />
            }
            content={
                <>
                    {hasVehicleSelected && (
                        <OpenDealCardInfo>
                            <SelectedVehicleInfo selectedVehicle={selectedVehicle} />
                        </OpenDealCardInfo>
                    )}
                    {editMode && (
                        <>
                            <ChangeVehicleTitle data-testid="change-vehicle-action-title">{actionText}</ChangeVehicleTitle>
                            <ChangeVehicleInterstate enableConfirmModal={false} forceSmallBreakpoint />
                        </>
                    )}
                </>
            }
        />
    );
};
